import {
  useCallback,
  useEffect,
  useId,
  useRef,
  useState,
  type PointerEvent as ReactPointerEvent,
  type ReactNode,
} from "react";
import { Download, Maximize2, Minimize2, RotateCcw, X, ZoomIn, ZoomOut } from "lucide-react";
import { images, resort } from "@/data/resort";
import { cn } from "@/lib/utils";
import { ResortLink } from "../resort-button";
import { Eyebrow, MaskReveal, Reveal } from "../reveal";

const MIN_SCALE = 1;
const MAX_SCALE = 5;
const ZOOM_STEP = 0.35;

const MAP_ALT =
  "Map of Auburn RV Resort showing RV site layout and resort facilities in Auburn, California";

type ViewerProps = {
  fullscreen?: boolean;
  onRequestFullscreen?: () => void;
  onCloseFullscreen?: () => void;
  className?: string;
};

function clamp(n: number, min: number, max: number) {
  return Math.min(max, Math.max(min, n));
}

function MapCanvas({
  fullscreen = false,
  onRequestFullscreen,
  onCloseFullscreen,
  className,
}: ViewerProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [scale, setScale] = useState(1);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const [active, setActive] = useState(false);
  const [hiRes, setHiRes] = useState(false);
  const dragRef = useRef<{
    pointerId: number;
    startX: number;
    startY: number;
    originX: number;
    originY: number;
  } | null>(null);
  const pinchRef = useRef<{
    distance: number;
    scale: number;
  } | null>(null);
  const pointers = useRef(new Map<number, { x: number; y: number }>());

  const reset = useCallback(() => {
    setScale(1);
    setOffset({ x: 0, y: 0 });
  }, []);

  const zoomBy = useCallback((delta: number, cx?: number, cy?: number) => {
    setHiRes(true);
    setScale((prev) => {
      const next = clamp(prev + delta, MIN_SCALE, MAX_SCALE);
      if (next === 1) {
        setOffset({ x: 0, y: 0 });
        return next;
      }
      if (cx != null && cy != null && containerRef.current) {
        const rect = containerRef.current.getBoundingClientRect();
        const px = cx - rect.width / 2;
        const py = cy - rect.height / 2;
        const ratio = next / prev;
        setOffset((o) => ({
          x: px - (px - o.x) * ratio,
          y: py - (py - o.y) * ratio,
        }));
      }
      return next;
    });
  }, []);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const onWheel = (e: WheelEvent) => {
      if (!active && !fullscreen) return;
      e.preventDefault();
      const rect = el.getBoundingClientRect();
      zoomBy(
        e.deltaY > 0 ? -ZOOM_STEP : ZOOM_STEP,
        e.clientX - rect.left,
        e.clientY - rect.top,
      );
    };
    el.addEventListener("wheel", onWheel, { passive: false });
    return () => el.removeEventListener("wheel", onWheel);
  }, [active, fullscreen, zoomBy]);

  const onPointerDown = (e: ReactPointerEvent) => {
    setActive(true);
    (e.target as HTMLElement).setPointerCapture?.(e.pointerId);
    pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY });

    if (pointers.current.size === 2) {
      const [p0, p1] = [...pointers.current.values()];
      if (!p0 || !p1) return;
      const dx = p0.x - p1.x;
      const dy = p0.y - p1.y;
      pinchRef.current = { distance: Math.hypot(dx, dy), scale };
      dragRef.current = null;
      return;
    }

    if (scale > 1) {
      dragRef.current = {
        pointerId: e.pointerId,
        startX: e.clientX,
        startY: e.clientY,
        originX: offset.x,
        originY: offset.y,
      };
    }
  };

  const onPointerMove = (e: ReactPointerEvent) => {
    if (!pointers.current.has(e.pointerId)) return;
    pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY });

    if (pointers.current.size === 2 && pinchRef.current) {
      const [p0, p1] = [...pointers.current.values()];
      if (!p0 || !p1) return;
      const dx = p0.x - p1.x;
      const dy = p0.y - p1.y;
      const dist = Math.hypot(dx, dy);
      const next = clamp(
        pinchRef.current.scale * (dist / Math.max(pinchRef.current.distance, 1)),
        MIN_SCALE,
        MAX_SCALE,
      );
      setHiRes(true);
      setScale(next);
      if (next === 1) setOffset({ x: 0, y: 0 });
      return;
    }

    const drag = dragRef.current;
    if (!drag || drag.pointerId !== e.pointerId || scale <= 1) return;
    setOffset({
      x: drag.originX + (e.clientX - drag.startX),
      y: drag.originY + (e.clientY - drag.startY),
    });
  };

  const onPointerUp = (e: ReactPointerEvent) => {
    pointers.current.delete(e.pointerId);
    if (pointers.current.size < 2) pinchRef.current = null;
    if (dragRef.current?.pointerId === e.pointerId) dragRef.current = null;
  };

  const onDoubleClick = (e: React.MouseEvent) => {
    const rect = containerRef.current?.getBoundingClientRect();
    if (!rect) return;
    if (scale > 1.2) {
      reset();
      return;
    }
    zoomBy(1.2, e.clientX - rect.left, e.clientY - rect.top);
  };

  const src = hiRes || fullscreen || scale > 1.05 ? images.resortMapFull : images.resortMapPreview;

  const controls = (
    <div
      className={cn(
        "flex flex-wrap items-center gap-2",
        fullscreen ? "absolute bottom-4 left-1/2 z-20 -translate-x-1/2" : "mt-4 justify-center",
      )}
    >
      <ControlButton label="Zoom in" onClick={() => zoomBy(ZOOM_STEP)}>
        <ZoomIn className="size-4" strokeWidth={1.6} />
      </ControlButton>
      <ControlButton label="Zoom out" onClick={() => zoomBy(-ZOOM_STEP)}>
        <ZoomOut className="size-4" strokeWidth={1.6} />
      </ControlButton>
      <ControlButton label="Reset map view" onClick={reset}>
        <RotateCcw className="size-4" strokeWidth={1.6} />
      </ControlButton>
      {fullscreen ? (
        <ControlButton label="Exit fullscreen map" onClick={onCloseFullscreen}>
          <Minimize2 className="size-4" strokeWidth={1.6} />
        </ControlButton>
      ) : (
        <ControlButton
          label="Open map fullscreen"
          onClick={() => {
            setHiRes(true);
            onRequestFullscreen?.();
          }}
        >
          <Maximize2 className="size-4" strokeWidth={1.6} />
        </ControlButton>
      )}
      <a
        href={images.resortMapFull}
        download="auburn-rv-resort-map.jpg"
        className="inline-flex size-11 items-center justify-center rounded-sm border border-border bg-background/95 text-foreground shadow-soft backdrop-blur-sm transition-colors hover:bg-primary hover:text-primary-foreground"
        aria-label="Download resort map"
      >
        <Download className="size-4" strokeWidth={1.6} />
      </a>
    </div>
  );

  return (
    <div className={cn("relative", className)}>
      <div
        ref={containerRef}
        tabIndex={0}
        role="img"
        aria-label={MAP_ALT}
        onFocus={() => setActive(true)}
        onBlur={() => setActive(false)}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
        onDoubleClick={onDoubleClick}
        onKeyDown={(e) => {
          if (e.key === "+" || e.key === "=") {
            e.preventDefault();
            zoomBy(ZOOM_STEP);
          } else if (e.key === "-" || e.key === "_") {
            e.preventDefault();
            zoomBy(-ZOOM_STEP);
          } else if (e.key === "0") {
            e.preventDefault();
            reset();
          }
        }}
        className={cn(
          "relative touch-none overflow-hidden border border-border bg-secondary/40 outline-none focus-visible:ring-2 focus-visible:ring-primary",
          fullscreen ? "h-[min(92svh,920px)] w-full" : "aspect-square w-full max-h-[78vh] md:aspect-[5/4] md:max-h-[80vh]",
          scale > 1 ? "cursor-grab active:cursor-grabbing" : "cursor-zoom-in",
        )}
      >
        <div
          className="flex size-full items-center justify-center transition-transform duration-75 ease-out will-change-transform"
          style={{
            transform: `translate(${offset.x}px, ${offset.y}px) scale(${scale})`,
          }}
        >
          <img
            src={src}
            alt={MAP_ALT}
            width={2197}
            height={2075}
            draggable={false}
            decoding="async"
            loading={fullscreen ? "eager" : "lazy"}
            className="max-h-full max-w-full select-none object-contain"
          />
        </div>
        {!fullscreen && (
          <p className="pointer-events-none absolute bottom-3 left-3 bg-background/90 px-3 py-1.5 text-[0.65rem] uppercase tracking-[0.14em] text-muted-foreground">
            Scroll or pinch to zoom · Drag to pan
          </p>
        )}
        {fullscreen && (
          <button
            type="button"
            onClick={onCloseFullscreen}
            aria-label="Close fullscreen map"
            className="absolute right-4 top-4 z-30 inline-flex size-11 items-center justify-center rounded-sm border border-border bg-background/95 text-foreground shadow-soft"
          >
            <X className="size-5" strokeWidth={1.6} />
          </button>
        )}
      </div>
      {controls}
    </div>
  );
}

function ControlButton({
  label,
  onClick,
  children,
}: {
  label: string;
  onClick?: (() => void) | undefined;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      aria-label={label}
      onClick={onClick}
      className="inline-flex size-11 items-center justify-center rounded-sm border border-border bg-background/95 text-foreground shadow-soft backdrop-blur-sm transition-colors hover:bg-primary hover:text-primary-foreground"
    >
      {children}
    </button>
  );
}

export function ResortMapViewer() {
  const [fullscreen, setFullscreen] = useState(false);
  const titleId = useId();
  const closeRef = useRef<HTMLButtonElement>(null);
  const previousFocus = useRef<HTMLElement | null>(null);

  useEffect(() => {
    if (!fullscreen) return;
    previousFocus.current = document.activeElement as HTMLElement | null;
    document.body.style.overflow = "hidden";
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setFullscreen(false);
    };
    window.addEventListener("keydown", onKey);
    closeRef.current?.focus();
    return () => {
      document.body.style.overflow = "";
      window.removeEventListener("keydown", onKey);
      previousFocus.current?.focus();
    };
  }, [fullscreen]);

  return (
    <section
      id="resort-map"
      aria-labelledby="map-viewer-heading"
      className="scroll-mt-16 bg-background py-14 md:py-18"
    >
      <div className="shell-wide mx-auto max-w-[1400px] px-4 sm:px-6 lg:px-8">
        <Reveal>
          <Eyebrow>Official Resort Map</Eyebrow>
        </Reveal>
        <h2 id="map-viewer-heading" className="display-lg mt-6 max-w-[16ch]">
          <MaskReveal>Take a Closer Look</MaskReveal>
        </h2>
        <Reveal delay={0.08}>
          <p className="mt-5 max-w-2xl text-base leading-relaxed text-muted-foreground">
            Zoom in to explore the Auburn RV Resort layout and site locations. This Auburn CA RV
            park map is informational — it does not show live inventory or reserve a site.
          </p>
        </Reveal>

        <div className="mt-10">
          <MapCanvas
            onRequestFullscreen={() => setFullscreen(true)}
            onCloseFullscreen={() => setFullscreen(false)}
          />
        </div>

        <div className="mt-8 flex flex-col gap-6 border border-border bg-secondary/40 p-6 md:flex-row md:items-center md:justify-between md:p-8">
          <div>
            <p className="eyebrow text-primary">Need Help Choosing an Area?</p>
            <p className="mt-3 max-w-xl text-sm leading-relaxed text-muted-foreground">
              Our team can help identify the best available site based on your RV length, stay
              duration and site preference.
            </p>
          </div>
          <div className="flex w-full flex-col gap-3 sm:w-auto sm:flex-row">
            <ResortLink variant="solid" href="/reservations/" className="w-full sm:w-auto">
              Check Availability
            </ResortLink>
            <ResortLink variant="outline" href={resort.phoneHref} className="w-full sm:w-auto">
              Call {resort.phone}
            </ResortLink>
          </div>
        </div>
      </div>

      {fullscreen ? (
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby={titleId}
          className="fixed inset-0 z-[90] flex items-center justify-center bg-forest-deep/95 p-3 md:p-6"
        >
          <div className="relative w-full max-w-[1400px]">
            <h2 id={titleId} className="sr-only">
              Fullscreen Auburn RV Resort map
            </h2>
            <button ref={closeRef} type="button" className="sr-only">
              Fullscreen map controls
            </button>
            <MapCanvas fullscreen onCloseFullscreen={() => setFullscreen(false)} />
          </div>
        </div>
      ) : null}
    </section>
  );
}
